<script setup lang="ts">
import { Head, router } from '@inertiajs/vue3'
import { ref, computed } from 'vue'
import DashboardLayout from '@/Layouts/DashboardLayout.vue'
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '@/Components/ui/card'
import {
  DollarSign,
  ChevronLeft,
  ChevronRight,
  CreditCard,
  Users,
  Wallet,
  TrendingUp,
  Crown,
  Sparkles,
  Star,
} from 'lucide-vue-next'

interface Payment {
  id: number
  student_name: string
  student_email: string
  student_avatar: string | null
  tier_name: string
  tier_slug: string
  gross_amount: number
  amount: number
  commission: number
  currency: string
  status: string
  status_display: string
  paid_at: string | null
  created_at: string
  badge: string | null
  badge_date: string | null
}

interface ChartItem {
  label: string
  amount: number
  pending: boolean
}

interface Dashboard {
  total_paid_to_date: number
  active_subscription_gross: number
  active_subscription_net: number
  active_subscription_count: number
  payout_this_week: number
  payout_this_week_gross: number
  payout_date: string
  forecast_next_week: number
  forecast_next_week_gross: number
  forecast_date: string
  commission_rate: number
  release_chart: ChartItem[]
}

interface History {
  period_label: string
  start_date: string
  end_date: string
  prev_date: string
  next_date: string
  can_go_prev: boolean
  can_go_next: boolean
  payments: Payment[]
  period_release_net: number | null
  period_release_gross: number | null
  period_release_date: string | null
}

interface Filters {
  date: string | null
}

const props = defineProps<{
  dashboard: Dashboard
  history: History
  filters: Filters
}>()

const isLoading = ref(false)

function formatCurrency(amount: number, currency: string = 'GBP'): string {
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: currency,
  }).format(amount)
}

function getTierIcon(tierSlug: string) {
  switch (tierSlug) {
    case 'gold':
      return Crown
    case 'silver':
      return Sparkles
    default:
      return Star
  }
}

function getTierColor(tierSlug: string): string {
  switch (tierSlug) {
    case 'gold':
      return 'text-yellow-600 bg-yellow-50'
    case 'silver':
      return 'text-gray-600 bg-gray-100'
    default:
      return 'text-blue-600 bg-blue-50'
  }
}

function getStatusColor(status: string): string {
  switch (status) {
    case 'succeeded':
      return 'text-green-700 bg-green-50'
    case 'failed':
      return 'text-red-700 bg-red-50'
    case 'pending':
      return 'text-yellow-700 bg-yellow-50'
    default:
      return 'text-gray-700 bg-gray-100'
  }
}

function navigate(date: string) {
  isLoading.value = true
  router.get('/teacher/earnings', {
    date: date,
  }, {
    preserveState: true,
    preserveScroll: true,
    onFinish: () => {
      isLoading.value = false
    },
  })
}

const isCurrentWeek = computed(() => !props.history.can_go_next)

function goToToday() {
  isLoading.value = true
  router.get('/teacher/earnings', {}, {
    preserveState: true,
    preserveScroll: true,
    onFinish: () => {
      isLoading.value = false
    },
  })
}

const maxChartAmount = computed(() => {
  return Math.max(...props.dashboard.release_chart.map(b => b.amount), 1)
})

</script>

<template>
  <Head title="Earnings" />

  <DashboardLayout>
    <div class="w-full max-w-6xl space-y-6">
      <div class="space-y-0.5">
        <h2 class="text-2xl font-bold tracking-tight">Earnings</h2>
        <p class="text-gray-500">Track your income and payment releases.</p>
      </div>

      <!-- Row 1: Total Earnings To Date -->
      <Card class="border-green-200 bg-green-50/50">
        <CardContent class="pt-6">
          <div class="flex items-center gap-4">
            <div class="p-3 bg-green-100 rounded-full">
              <DollarSign class="w-6 h-6 text-green-600" />
            </div>
            <div>
              <p class="text-sm font-medium text-green-900">Total Earnings To Date</p>
              <p class="text-3xl font-bold text-green-700">{{ formatCurrency(dashboard.total_paid_to_date) }}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      <!-- Row 2: Payment Release This Week (3 cards) -->
      <div class="grid gap-4 md:grid-cols-3">
        <Card>
          <CardHeader class="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle class="text-sm font-medium">Active Subscriptions</CardTitle>
            <Users class="h-4 w-4 text-blue-500" />
          </CardHeader>
          <CardContent>
            <div class="text-2xl font-bold">{{ formatCurrency(dashboard.active_subscription_net) }}</div>
            <p class="text-xs text-gray-500">{{ formatCurrency(dashboard.active_subscription_gross) }} gross &middot; {{ dashboard.active_subscription_count }} student{{ dashboard.active_subscription_count !== 1 ? 's' : '' }} weekly</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader class="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle class="text-sm font-medium">Payout This Week</CardTitle>
            <Wallet class="h-4 w-4 text-green-500" />
          </CardHeader>
          <CardContent>
            <div class="text-2xl font-bold text-green-600">{{ formatCurrency(dashboard.payout_this_week) }}</div>
            <p class="text-xs text-gray-500">{{ dashboard.payout_date }}</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader class="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle class="text-sm font-medium">Forecast Next Week</CardTitle>
            <TrendingUp class="h-4 w-4 text-orange-500" />
          </CardHeader>
          <CardContent>
            <div class="text-2xl font-bold text-orange-600">{{ formatCurrency(dashboard.forecast_next_week) }}</div>
            <p class="text-xs text-gray-500">{{ dashboard.forecast_date }}</p>
          </CardContent>
        </Card>
      </div>

      <!-- Row 3: Payment Release History (chart) — hidden when all weeks are zero -->
      <Card v-if="dashboard.release_chart.length > 0">
        <CardHeader>
          <CardTitle>Payment Release History</CardTitle>
          <CardDescription>Weekly net payouts</CardDescription>
        </CardHeader>
        <CardContent>
          <div class="flex items-end justify-center gap-2 h-48">
            <div
              v-for="(item, index) in dashboard.release_chart"
              :key="index"
              class="flex flex-col items-center gap-1 w-12.5 max-w-12.5"
            >
              <div class="relative w-full flex justify-center">
                <div
                  :class="[
                    'w-full rounded-t-sm transition-all',
                    item.pending
                      ? 'bg-blue-200 border border-dashed border-blue-400 hover:bg-blue-300'
                      : 'bg-blue-500 hover:bg-blue-600'
                  ]"
                  :style="{ height: `${Math.max((item.amount / maxChartAmount) * 160, 4)}px` }"
                  :title="`${item.label}: ${formatCurrency(item.amount)}${item.pending ? ' (expected)' : ''}`"
                ></div>
              </div>
              <div class="text-xs text-gray-500 text-center">
                <div class="font-medium">{{ item.label }}</div>
                <div :class="item.pending ? 'text-blue-400' : 'text-gray-400'">
                  {{ formatCurrency(item.amount) }}

                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <!-- Row 4: Subscription History -->
      <Card>
        <CardHeader>
          <div class="flex flex-col gap-4 md:gap-0 md:flex-row md:items-center justify-between">
            <div>
              <CardTitle>Subscription History</CardTitle>
              <CardDescription>
                {{ history.payments.length }} payment{{ history.payments.length !== 1 ? 's' : '' }} this week
              </CardDescription>
            </div>

            <!-- Week Navigation -->
            <div class="flex items-center md:">
              <button
                @click="navigate(history.prev_date)"
                :disabled="!history.can_go_prev || isLoading"
                :class="[
                  'p-2 rounded-lg transition-colors',
                  history.can_go_prev && !isLoading
                    ? 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                    : 'text-gray-300 cursor-not-allowed'
                ]"
              >
                <ChevronLeft class="w-5 h-5" />
              </button>
              <span class="font-medium text-gray-900 min-w-50 text-center text-sm">
                {{ history.period_label }}
              </span>
              <button
                v-if="!isCurrentWeek"
                @click="goToToday"
                :disabled="isLoading"
                class="px-2.5 py-1 text-xs font-medium text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors"
              >
                Today
              </button>
              <button
                @click="navigate(history.next_date)"
                :disabled="!history.can_go_next || isLoading"
                :class="[
                  'p-2 rounded-lg transition-colors',
                  history.can_go_next && !isLoading
                    ? 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                    : 'text-gray-300 cursor-not-allowed'
                ]"
              >
                <ChevronRight class="w-5 h-5" />
              </button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div v-if="isLoading" class="flex items-center justify-center py-12">
            <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>

          <div v-else-if="history.payments.length === 0" class="text-center py-12 text-gray-400">
            <CreditCard class="w-12 h-12 mx-auto mb-3 opacity-50" />
            <p class="mb-2 font-medium text-gray-600">No payments this week</p>
            <p class="text-sm">Payments will appear here once students are billed.</p>
          </div>

          <div v-else>
            <!-- Period release banner -->
            <div v-if="history.period_release_net !== null" class="flex items-center justify-between mb-4 pb-4 border-b border-gray-100">
              <div class="flex items-center gap-2">
                <span class="inline-flex items-center px-2 py-1 rounded text-xs font-medium text-green-700 bg-green-50">Released this week</span>
                <span class="text-xs text-gray-400">{{ history.period_release_date }}</span>
              </div>
              <div class="text-right">
                <span class="text-sm font-bold text-green-700">{{ formatCurrency(history.period_release_net) }}</span>
                <span class="text-xs text-gray-400 ml-1">({{ formatCurrency(history.period_release_gross) }} gross)</span>
              </div>
            </div>
            <div v-else class="flex items-center gap-2 mb-4 pb-4 border-b border-gray-100">
              <span class="inline-flex items-center px-2 py-1 rounded text-xs font-medium text-gray-500 bg-gray-50">No release this week</span>
            </div>

            <!-- Payment list -->
            <div class="divide-y divide-gray-100">
              <div
                v-for="payment in history.payments"
                :key="payment.id"
                class="flex items-center justify-between py-4"
              >
                <div class="flex items-center gap-4">
                  <!-- Avatar -->
                  <div class="shrink-0">
                    <img
                      v-if="payment.student_avatar"
                      :src="payment.student_avatar"
                      :alt="payment.student_name"
                      class="w-10 h-10 rounded-full object-cover"
                    />
                    <div v-else class="w-10 h-10 rounded-full bg-linear-to-br from-teal-500 to-teal-800 flex items-center justify-center text-white font-semibold">
                      {{ payment.student_name.charAt(0).toUpperCase() }}
                    </div>
                  </div>

                  <!-- Info -->
                  <div>
                    <div class="flex items-center gap-2">
                      <h4 class="font-medium text-gray-900">{{ payment.student_name }}</h4>
                      <span :class="[getTierColor(payment.tier_slug), 'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium']">
                        <component :is="getTierIcon(payment.tier_slug)" class="w-3 h-3" />
                        {{ payment.tier_name }}
                      </span>
                    </div>
                    <div class="text-sm text-gray-500">
                      Paid on {{ payment.paid_at || payment.created_at }}
                    </div>
                  </div>
                </div>

                <div class="flex items-center gap-4">
                  <!-- Release status badge -->
                  <div class="text-right min-w-28">
                    <span
                      v-if="payment.badge === 'paid'"
                      class="inline-flex items-center px-2 py-1 rounded text-xs font-medium text-green-700 bg-green-50"
                    >
                      Paid
                    </span>
                    <span
                      v-else-if="payment.badge === 'to_be_paid'"
                      class="inline-flex items-center px-2 py-1 rounded text-xs font-medium text-blue-700 bg-blue-50"
                    >
                      To be paid
                    </span>
                    <span
                      v-else-if="payment.badge === 'to_be_paid_future'"
                      class="inline-flex items-center px-2 py-1 rounded text-xs font-medium text-red-700 bg-red-50"
                    >
                      To be paid
                    </span>
                    <span v-else :class="[getStatusColor(payment.status), 'px-2 py-1 rounded text-xs font-medium']">
                      {{ payment.status_display }}
                    </span>
                    <div v-if="payment.badge_date" class="text-xs text-gray-400 mt-0.5">
                      <template v-if="payment.badge === 'paid'">Released {{ payment.badge_date }}</template>
                      <template v-else-if="payment.badge === 'to_be_paid'">Due {{ payment.badge_date }}</template>
                      <template v-else-if="payment.badge === 'to_be_paid_future'">Expected {{ payment.badge_date }}</template>
                      <template v-else>{{ payment.badge_date }}</template>
                    </div>
                  </div>
                  <div class="text-right min-w-25">
                    <span class="font-semibold text-gray-900">
                      {{ formatCurrency(payment.amount, payment.currency) }}
                    </span>
                    <div class="text-xs text-gray-400">
                      {{ formatCurrency(payment.gross_amount, payment.currency) }} gross
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Week release total -->
            <div v-if="history.period_release_net !== null" class="flex items-center justify-between pt-4 mt-2 border-t border-gray-200">
              <span class="text-sm font-medium text-gray-600">Released to you this week</span>
              <span class="text-base font-bold text-green-700">{{ formatCurrency(history.period_release_net) }}</span>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  </DashboardLayout>
</template>
